import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
} from "ag-grid-community";
import { Header } from "@aptsi-types";

@Component({
  selector: "app-insight-egrc-table",
  templateUrl: "./insight-egrc-table.component.html",
  styleUrls: ["./insight-egrc-table.component.scss"],
})
export class InsightEgrcTableComponent implements OnInit, OnChanges {
  @Input() headers: Header[] = [];
  @Input() data: any[] = [];
  @Input() columnDefs: ColDef[] = [];
  @Input() buttonConfig: Record<string, boolean> = {};

  @Output() actionClicked = new EventEmitter<{
    action: string;
    rowIndex: number;
    rowData: any;
  }>();
  @Output() rowClicked = new EventEmitter<{
    rowIndex: number;
    rowData: any;
  }>();

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 200,
    filter: true,
    tooltipValueGetter: (params) => this.getTooltip(params),
  };

  private gridApi: GridApi | null = null;

  ngOnInit(): void {
    this.buildColumnDefs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.headers || changes.buttonConfig) {
      this.buildColumnDefs();
    }

    if (changes.data && this.gridApi) {
      setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  onCellClicked(event: CellClickedEvent): void {
    if (event.column?.getColId() !== "actions") {
      return;
    }

    const target = event.event?.target as HTMLElement | null;
    const actionButton = target?.closest("[data-action]") as HTMLElement | null;
    if (!actionButton) {
      return;
    }

    event.event?.stopPropagation();
    event.event?.preventDefault();

    const action = actionButton.getAttribute("data-action");
    if (!action || !event.data) {
      return;
    }

    this.actionClicked.emit({
      action,
      rowIndex: event.node?.rowIndex ?? -1,
      rowData: event.data,
    });
  }

  onRowClicked(event: RowClickedEvent): void {
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest("[data-action], button, .btn, .insight-egrc-action-btn")) {
      return;
    }

    if (!event.data) {
      return;
    }

    this.rowClicked.emit({
      rowIndex: event.rowIndex ?? -1,
      rowData: event.data,
    });
  }

  getTooltip(params: any): string {
    if (!params.node || !params.node.data) return "";

    const columnDefs = params.api.getColumnDefs();
    const visibleColumns = columnDefs
      .filter((col: any) => !col.hide)
      .map((col: any) => `${col.headerName}: ${params.node.data[col.field]}`)
      .join("\n");

    return visibleColumns;
  }

  private buildColumnDefs(): void {
    this.columnDefs = this.headers.map((header) => ({
      headerName: header.name,
      field: header.property,
    }));

    if (this.hasActionButtons()) {
      this.columnDefs.unshift({
        headerName: "Actions",
        colId: "actions",
        field: "actions",
        cellRenderer: () => this.renderActionButtonsHtml(),
        sortable: false,
        filter: false,
        width: 200,
      });
    }
  }

  private renderActionButtonsHtml(): string {
    const buttons: string[] = [];

    if (this.buttonConfig.update) {
      buttons.push(
        `<button type="button" class="btn insight-egrc-action-btn" data-action="update" title="Edit">
          <i class="bi bi-pencil-fill" style="color: grey;"></i>
        </button>`,
      );
    }

    if (this.buttonConfig.delete) {
      buttons.push(
        `<button type="button" class="btn insight-egrc-action-btn" data-action="delete" title="Delete">
          <i class="bi bi-trash" style="color: rgb(224, 71, 71);"></i>
        </button>`,
      );
    }

    return `<div style="display:flex;align-items:center;justify-content:center;gap:8px;height:100%;">${buttons.join("")}</div>`;
  }

  private hasActionButtons(): boolean {
    return !!(
      this.buttonConfig.update ||
      this.buttonConfig.delete ||
      this.buttonConfig.import ||
      this.buttonConfig.configure
    );
  }
}
